
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import apiClient from "../utils/apiClient";

const WebsiteSettings = () => {

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [editId, setEditId] = useState("");

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState({
    sportName: "",

    emailId: "",
    supportEmail: "",
    phoneNumber: "",
    whatsappNumber: "",
    address: "",

    telegramLink: "",
    instagramLink: "",
    facebookLink: "",
    twitterLink: "",
    youtubeLink: "",
    linkedinLink: "",
    discordLink: "",
    snapchatLink: "",
    pinterestLink: "",
    redditLink: "",

    androidAppLink: "",
    iosAppLink: "",

    maintenanceMode: false,

    importantDisclaimer: "",
  });

  // =====================================
  // HANDLE CHANGE
  // =====================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================
  // GET SETTINGS
  // =====================================

  const getSettings = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get(
        "/api/admin/get-website-settings"
      );

      const result = response?.data;

      if (result?.success && result?.data) {

        setEditId(result?.data?.id);

        setFormData({
          sportName:
            result?.data?.sportName || "",

          emailId:
            result?.data?.emailId || "",

          supportEmail:
            result?.data?.supportEmail || "",

          phoneNumber:
            result?.data?.phoneNumber || "",

          whatsappNumber:
            result?.data?.whatsappNumber || "",

          address:
            result?.data?.address || "",

          telegramLink:
            result?.data?.telegramLink || "",

          instagramLink:
            result?.data?.instagramLink || "",

          facebookLink:
            result?.data?.facebookLink || "",

          twitterLink:
            result?.data?.twitterLink || "",

          youtubeLink:
            result?.data?.youtubeLink || "",

          linkedinLink:
            result?.data?.linkedinLink || "",

          discordLink:
            result?.data?.discordLink || "",

          snapchatLink:
            result?.data?.snapchatLink || "",

          pinterestLink:
            result?.data?.pinterestLink || "",

          redditLink:
            result?.data?.redditLink || "",

          androidAppLink:
            result?.data?.androidAppLink || "",

          iosAppLink:
            result?.data?.iosAppLink || "",

          maintenanceMode:
            result?.data?.maintenanceMode || false,

          importantDisclaimer:
            result?.data?.importantDisclaimer || "",
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  // =====================================
  // VALIDATION
  // =====================================

  const validateForm = () => {

    if (!formData.sportName) {
      showToast("Sport name is required", "error");
      return false;
    }

    if (
      formData.emailId &&
      !/^\S+@\S+\.\S+$/.test(formData.emailId)
    ) {
      showToast("Invalid email address", "error");
      return false;
    }

    return true;
  };

  // =====================================
  // TOAST
  // =====================================

  const showToast = (
    message,
    severity = "success"
  ) => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  // =====================================
  // SUBMIT
  // =====================================

  const handleSubmit = async () => {
    try {

      if (!validateForm()) {
        return;
      }

      setSubmitLoading(true);

      const payload = {
        ...formData,
      };

      let response;

      // UPDATE
      if (editId) {

        response = await apiClient.put(
          `/api/admin/update-website-settings/${editId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

      } else {

        // ADD
        response = await apiClient.post(
          `/api/admin/add-website-settings`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      const result = response?.data;

      if (result?.success) {

        showToast(result?.message);

        getSettings();
      }

    } catch (error) {

      showToast(
        error?.response?.data?.message ||
          "Something went wrong",
        "error"
      );

    } finally {
      setSubmitLoading(false);
    }
  };

  // =====================================
  // INPUT FIELD
  // =====================================

  const renderInput = (
    label,
    name,
    multiline = false
  ) => (
    <TextField
      fullWidth
      label={label}
      name={name}
      value={formData[name]}
      onChange={handleChange}
      multiline={multiline}
      rows={multiline ? 4 : 1}
      size="small"
    />
  );

  if (loading) {
    return (
      <Box className="flex justify-center py-10">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }}>

      <Card
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 4,
        }}
      >

        <Typography
          variant="h5"
          fontWeight={700}
          mb={3}
        >
          Website Settings
        </Typography>

        <Grid container spacing={3}>

          {/* ===================================== */}
          {/* WEBSITE DETAILS */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Website Details
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "Sport Name",
              "sportName"
            )}
          </Grid>

          {/* ===================================== */}
          {/* CONTACT DETAILS */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Contact Details
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item size={{xs:12, md:6}} >
            {renderInput(
              "Email",
              "emailId"
            )}
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "Support Email",
              "supportEmail"
            )}
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "Phone Number",
              "phoneNumber"
            )}
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "WhatsApp Number",
              "whatsappNumber"
            )}
          </Grid>

          <Grid item size={{xs:12}}>
            {renderInput(
              "Address",
              "address",
              true
            )}
          </Grid>

          {/* ===================================== */}
          {/* SOCIAL LINKS */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Social Media Links
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          {[
            ["Telegram Link", "telegramLink"],
            ["Instagram Link", "instagramLink"],
            ["Facebook Link", "facebookLink"],
            ["Twitter Link", "twitterLink"],
            ["YouTube Link", "youtubeLink"],
            ["LinkedIn Link", "linkedinLink"],
            ["Discord Link", "discordLink"],
            ["Snapchat Link", "snapchatLink"],
            ["Pinterest Link", "pinterestLink"],
            ["Reddit Link", "redditLink"],
          ].map(([label, name]) => (
            <Grid item size={{xs:12, md:6}} key={name}>
              {renderInput(label, name)}
            </Grid>
          ))}

          {/* ===================================== */}
          {/* APP LINKS */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Mobile Apps
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "Android App Link",
              "androidAppLink"
            )}
          </Grid>

          <Grid item size={{xs:12, md:6}}>
            {renderInput(
              "iOS App Link",
              "iosAppLink"
            )}
          </Grid>

          {/* ===================================== */}
          {/* DISCLAIMER */}
          {/* ===================================== */}

          <Grid item size={{xs:12}} >
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Disclaimer
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item size={{xs:12}}>
            {renderInput(
              "Important Disclaimer",
              "importantDisclaimer",
              true
            )}
          </Grid>

          {/* ===================================== */}
          {/* SETTINGS */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Other Settings
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item size={{xs:12}}>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    formData.maintenanceMode
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      maintenanceMode:
                        e.target.checked,
                    }))
                  }
                />
              }
              label="Maintenance Mode"
            />
          </Grid>

          {/* ===================================== */}
          {/* SUBMIT */}
          {/* ===================================== */}

          <Grid item size={{xs:12}}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={submitLoading}
              sx={{
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
              }}
            >
              {submitLoading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : editId ? (
                "Update Settings"
              ) : (
                "Save Settings"
              )}
            </Button>
          </Grid>

        </Grid>

      </Card>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() =>
          setToast((prev) => ({
            ...prev,
            open: false,
          }))
        }
      >
        <Alert
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default WebsiteSettings;

